import { Point2D, Vector2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import { findAxisSnapTargetByKey, PreComputedAxisSnapTargets } from './collectAxisSnapTargets';
import { ENTITY_SOFT_LOCK, FOOT_GRID_INCREMENT_INCHES, FOOT_GRID_SNAP_RADIUS, GRID_SOFT_LOCK } from './config';
import { getAxisValue, resolveAxisSnapTarget } from './helpers';
import { InteractiveEditSnappingSettings } from './settings';
import { resolveSoftLockedCandidate } from './softLocking';
import {
  AxisSnapTarget,
  AxisSnapTargetKey,
  GridSnapTarget,
  gridSnapTargetEquals,
  MoveAxisSoftLockState,
  MoveSoftLockState,
  SnapAxis,
} from './types';
import {
  chooseClosestByDistance,
  chooseHigherPriorityCandidate,
  getVertexCandidateWeight,
  RankedVertexCandidate,
} from './vertexCandidateRanking';

export interface MoveSnapResult {
  readonly delta: Vector2D;
  readonly softLockState: MoveSoftLockState;
}

/**
 * Computes a snapped movement delta for a set of shape vertices being moved together during an interactive move edit.
 *
 * Translates `vertices` by `movementDelta`, snaps the moved positions to the given targets, and returns the total delta
 *   (movement + snap correction) that should be applied to all edited shapes by the caller. Takes and returns an
 *   updated `MoveSoftLockState` - its memory for sticky snapping behaviors.
 */
export function snapPointPositionsDuringInteractiveMoveEdit(
  vertices: readonly Point2D[],
  movementDelta: Vector2D,
  snapTargets: PreComputedAxisSnapTargets,
  softLockState: MoveSoftLockState,
  settings: InteractiveEditSnappingSettings,
): MoveSnapResult {
  vertices = vertices.map((vertex) => vertex.translate(movementDelta));

  const xSnap = new MoveAxisSnapSearch(vertices, snapTargets, 'x', softLockState.x, settings).findBestResult();
  const ySnap = new MoveAxisSnapSearch(vertices, snapTargets, 'y', softLockState.y, settings).findBestResult();

  return {
    delta: movementDelta.add(new Vector2D(xSnap.delta, ySnap.delta)),
    softLockState: { x: xSnap.softLockState, y: ySnap.softLockState },
  };
}

class MoveAxisSnapSearch {
  private readonly gridRawValues: readonly number[];
  private readonly axisSnapTargets: readonly AxisSnapTarget[];

  constructor(
    private readonly sourceCorners: readonly Point2D[],
    private readonly allTargets: PreComputedAxisSnapTargets,
    private readonly axis: SnapAxis,
    private readonly previous: MoveAxisSoftLockState,
    private readonly settings: InteractiveEditSnappingSettings,
  ) {
    this.gridRawValues = sourceCorners.map((corner) => getAxisValue(corner, axis));
    this.axisSnapTargets = allTargets[axis];
  }

  findBestResult(): { delta: number; softLockState: MoveAxisSoftLockState } {
    const gridCandidate = this.settings.footGrid ? this.chooseGridCandidate() : null;
    const baselineGridDelta = gridCandidate?.delta ?? 0;
    const entityCandidate = this.chooseEntityCandidate(baselineGridDelta);
    const winner = chooseHigherPriorityCandidate<MoveAxisVertexCandidate>(entityCandidate, gridCandidate);

    return {
      delta: winner?.delta ?? 0,
      softLockState: toMoveAxisSoftLockState(winner),
    };
  }

  private chooseGridCandidate(): GridMoveCandidate | null {
    const previousGridTarget = this.previous?.family === 'grid' ? this.previous.target : null;
    const bestCandidate = this.findBestFootGridCandidate();
    const lockedCandidate = previousGridTarget != null ? this.findLockedGridCandidate(previousGridTarget) : null;

    return resolveSoftLockedCandidate(
      lockedCandidate,
      bestCandidate,
      (a, b) => gridSnapTargetEquals(a.target, b.target),
      previousGridTarget != null,
      GRID_SOFT_LOCK,
    );
  }

  private findBestFootGridCandidate(): GridMoveCandidate | null {
    let best = null;

    for (const rawValue of this.gridRawValues) {
      best = chooseHigherPriorityCandidate(best, tryCreateFootGridCandidate(rawValue));
    }

    return best;
  }

  private findLockedGridCandidate(target: GridSnapTarget): GridMoveCandidate | null {
    let best = null;

    for (const rawValue of this.gridRawValues) {
      best = chooseClosestByDistance(best, createLockedGridCandidate(rawValue, target.snappedValue));
    }

    return best;
  }

  private chooseEntityCandidate(baselineGridDelta: number): EntityMoveCandidate | null {
    if (this.sourceCorners.length === 0 || this.axisSnapTargets.length === 0) {
      return null;
    }

    const previousEntityTargetKey = this.previous?.family === 'entity' ? this.previous.targetKey : null;
    const bestCandidate = this.findBestEntityCandidateAmong(this.axisSnapTargets, baselineGridDelta);
    const lockedTarget =
      previousEntityTargetKey != null ? findAxisSnapTargetByKey(this.allTargets, previousEntityTargetKey) : null;
    const lockedCandidate =
      lockedTarget != null && lockedTarget.axis === this.axis
        ? this.findBestEntityCandidateAmong([lockedTarget], baselineGridDelta)
        : null;

    return resolveSoftLockedCandidate(
      lockedCandidate,
      bestCandidate,
      (a, b) => a.targetKey === b.targetKey,
      previousEntityTargetKey != null,
      ENTITY_SOFT_LOCK,
    );
  }

  private findBestEntityCandidateAmong(
    targets: readonly AxisSnapTarget[],
    baselineGridDelta: number,
  ): EntityMoveCandidate | null {
    let best: EntityMoveCandidate | null = null;

    for (const sourceCorner of this.sourceCorners) {
      for (const target of targets) {
        best = chooseHigherPriorityCandidate(best, createEntityCandidate(sourceCorner, target, baselineGridDelta));
      }
    }

    return best;
  }
}

interface MoveCandidateBase extends RankedVertexCandidate {
  readonly delta: number;
}

interface GridMoveCandidate extends MoveCandidateBase {
  readonly family: 'grid';
  readonly target: GridSnapTarget;
}

interface EntityMoveCandidate extends MoveCandidateBase {
  readonly family: 'entity';
  readonly targetKey: AxisSnapTargetKey;
}

type MoveAxisVertexCandidate = GridMoveCandidate | EntityMoveCandidate;

function tryCreateFootGridCandidate(rawValue: number): GridMoveCandidate | null {
  const snappedValue = roundToIncrement(rawValue, FOOT_GRID_INCREMENT_INCHES);
  const delta = snappedValue - rawValue;
  const distance = Math.abs(delta);
  if (distance > FOOT_GRID_SNAP_RADIUS) {
    return null;
  }

  return {
    family: 'grid',
    delta,
    distance,
    weight: getVertexCandidateWeight(distance),
    target: { snappedValue },
  };
}

function createLockedGridCandidate(rawValue: number, snappedValue: number): GridMoveCandidate {
  const delta = snappedValue - rawValue;
  const distance = Math.abs(delta);
  const weight = getVertexCandidateWeight(distance);

  return { family: 'grid', delta, distance, weight, target: { snappedValue } };
}

function createEntityCandidate(
  sourceCorner: Point2D,
  target: AxisSnapTarget,
  baselineGridDelta: number,
): EntityMoveCandidate | null {
  const resolvedTarget = resolveAxisSnapTarget(target, sourceCorner);
  if (resolvedTarget === null) return null;

  const delta = resolvedTarget.axisTarget - getAxisValue(sourceCorner, target.axis);
  const distance = Math.abs(delta - baselineGridDelta);
  const weight = getVertexCandidateWeight(sourceCorner.distanceTo(resolvedTarget.snapLocation));

  return { family: 'entity', delta, distance, weight, targetKey: target.key };
}

function toMoveAxisSoftLockState(candidate: MoveAxisVertexCandidate | null): MoveAxisSoftLockState {
  if (candidate == null) return null;

  switch (candidate.family) {
    case 'entity':
      return { family: 'entity', targetKey: candidate.targetKey };

    case 'grid':
      return { family: 'grid', target: candidate.target };

    default:
      assertExhaustiveSwitch(candidate);
  }
}
